import { PaginatedResponse } from '@/types';
import { apiSlice } from './apiSlice';

/* ===================== TYPES ===================== */

export interface Vendor {
  id: number;
  name: string;
  category: 'Legal' | 'Compliance';
  email: string;
  phone?: string;
  website?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

/* ===================== API ===================== */

export const vendorApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ===================== GET VENDORS ===================== */
    getVendors: builder.query<
      PaginatedResponse<Vendor>,
      {
        skip?: number;
        take?: number;
        search?: string;
        category?: 'Legal' | 'Compliance';
      }
    >({
      query: ({ skip = 0, take = 10, search, category }) => ({
        url: '/accounts',
        params: {
          skip,
          take,
          ...(search ? { search } : {}),
          ...(category ? { category } : {}),
        },
      }),
      providesTags: ['Vendors'],
    }),

    /* ===================== GET VENDOR BY ID ===================== */
    getVendorById: builder.query<Vendor, number>({
      query: (vendorId) => `/vendors/${vendorId}`,
      providesTags: ['Vendors'],
    }),

    /* ===================== CREATE VENDOR (ADMIN) ===================== */
    createVendor: builder.mutation<
      Vendor,
      {
        name: string;
        category: 'Legal' | 'Compliance';
        email: string;
        phone?: string;
        website?: string;
      }
    >({
      query: (body) => ({
        url: '/vendors',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Vendors'],
    }),

    /* ===================== UPDATE VENDOR STATUS ===================== */
    updateVendorStatus: builder.mutation<
      void,
      {
        vendorId: number;
        isActive?: boolean;
        isDeleted?: boolean;
      }
    >({
      query: ({ vendorId, ...body }) => ({
        url: `/vendors/${vendorId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Vendors'],
    }),
  }),
});

/* ===================== HOOKS ===================== */

export const {
  useGetVendorsQuery,
  useGetVendorByIdQuery,
  useCreateVendorMutation,
  useUpdateVendorStatusMutation,
} = vendorApi;
